import React, { useState, useEffect, useRef } from 'react';

import { Box } from '@wds/box';
import { Zoom } from '@wds/motion';
import { Button } from '@wds/button';
import { Typography } from '@wds/typography';

import PropTypes from 'prop-types';
import TOTPEnrollment from './components/TOTPEnrollment';
import Challenge from './components/Challenge';
import EnrollmentComplete from './components/EnrollmentComplete';
import EmailEnrollment from './components/EmailEnrollment';

import STRINGS from './constants';

import { isSafeRedirectUrl } from '../../../sma.common/utils';

const RESEND_COOLDOWN_SEC = 120;

const getCookie = (name) => {
	const value = `; ${document.cookie}`;
	const parts = value.split(`; ${name}=`);
	if (parts.length === 2) {
		return parts.pop().split(';').shift();
	}
	return '';
};

const methodMap = {
	0: 'totp',
	1: 'email',
};

const MFA = (props) => {
	const { isModal, handleClose, setCurrentView, isChange } = props;
	const defaultContext = {
		challengeCode: '',
		isRememberDevice: false,
		qrCodeString: '',
		recoveryCodes: [],
		securityKeyString: '',
		status: '',
	};

	const [context, setContext] = useState(defaultContext);
	const [method, setMethod] = useState('');
	const [methods, setMethods] = useState();
	const [isLoaded, setIsLoaded] = useState(false);
	const [currentPage, setCurrentPage] = useState();
	const [error, setError] = useState('');
	const [nextUrl, setNextUrl] = useState('');
	const [isSliding, setIsSliding] = useState(false);
	const [isZooming, setIsZooming] = useState(false);
	const [sending, setSending] = useState(false);
	const [cooldown, setCooldown] = useState(0);
	const [message, setMessage] = useState('');
	const sentOnceRef = useRef(false);
	const timerRef = useRef(null);

	useEffect(() => {
		setIsLoaded(true);
		if (!isChange) {
			getNextUrl();
		}
		getEnrolledMethods();
		if (timerRef.current) {
			clearInterval(timerRef.current);
		}
	}, []);

	useEffect(() => {
		if (method === 'email' && !sentOnceRef.current) {
			if (isChange) {
				sentOnceRef.current = true;
				if (context?.status === 'code_pending') {
					setMessage(STRINGS.email_sent);
					startCooldown();
				}
			}
			if (!isChange && currentPage === 'challenge') {
				sendEmailCode();
			}
		}
	}, [method]);

	useEffect(() => {
		setMessage('');
		if (currentPage === 'totp-enrollment') {
			if (!context.qrCodeString) {
				getSecurityKey();
			}
			setMethod('totp');
		} else if (currentPage === 'email-enrollment') {
			setMethod('email');
		} else if (currentPage === 'challenge' && method === 'email' && !sentOnceRef.current) {
			sendEmailCode();
		}
	}, [currentPage]);

	const getNextUrl = () => {
		const urlParams = new URLSearchParams(window.location.search);
		if (urlParams.has('next')) {
			setNextUrl(urlParams.get('next'));
		}
	};

	const getEnrolledMethods = async () => {
		try {
			const response = await fetch(reverse('api-internal-mfa-enroll'), {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRFToken': getCookie('csrftoken'),
				},
			});
			if (!response.ok) {
				await handleErrorResponse(response);
				return;
			}
			const data = await response.json();
			const enrolledMethods = data.enrolled_methods || [];
			setContext((prev) => ({
				...prev,
				methods: enrolledMethods,
			}));
			setMethods(methods);
			if (isModal) {
				if (enrolledMethods.length === 1) {
					setMethod(methodMap[enrolledMethods[0].method]);
					setCurrentPage('challenge');
				} else if (enrolledMethods.length > 1) {
					setCurrentPage('method-selection');
				} else {
					setCurrentPage('totp-enrollment');
				}
			} else if (enrolledMethods.some((m) => m.method === 0 || m.method === 1)) {
				setMethod(methodMap[enrolledMethods[0].method]);
				setCurrentPage('challenge');
			} else {
				setCurrentPage('totp-enrollment');
			}
		} catch (err) {
			handleError(`Error fetching enrolled methods: ${err}`);
		}
	};

	const startCooldown = (remaining) => {
		if (remaining) {
			setCooldown(remaining);
		} else {
			setCooldown(RESEND_COOLDOWN_SEC);
		}
		if (timerRef.current) clearInterval(timerRef.current);
		timerRef.current = setInterval(() => {
			setCooldown((s) => {
				if (s <= 1) {
					clearInterval(timerRef.current);
					return 0;
				}
				return s - 1;
			});
		}, 1000);
	};

	const sendEmailCode = async () => {
		if (sending || cooldown > 0) return;
		setSending(true);
		setMessage('');
		setError('');
		try {
			let url;
			if (isChange) {
				url = reverse('api-internal-mfa-change-device');
			} else {
				url = reverse('api-internal-mfa-challenge');
			}
			const resp = await fetch(url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRFToken': getCookie('csrftoken'),
				},
				body: JSON.stringify({ action: 'send_code', method: 'email' }),
				credentials: 'same-origin',
			});
			const data = await resp.json();
			if (data.success) {
				setMessage(data.message);
				sentOnceRef.current = true;
				startCooldown();
			} else if (data.message) {
				setMessage(STRINGS?.[data.message]);
			} else if (data.error) {
				if (data.error === 'invalid_code_locked_out' || data.error === 'not_authenticated') {
					window.location.href = reverse('mfa-lockout');
				} else if (data.error === 'code_already_pending') {
					setContext({
						...context,
						challengeCode: '',
						status: 'code_already_pending',
					});
					setError(STRINGS.code_already_pending);
					startCooldown(data.time_remaining);
				} else {
					setError(STRINGS?.[data.error] || STRINGS.unknownError);
				}
			} else {
				setMessage(STRINGS.unknownError);
			}
		} catch (err) {
			console.log(err);
			handleError(STRINGS.unknownError);
		} finally {
			setSending(false);
		}
	};

	const getSecurityKey = async () => {
		try {
			let url;
			let action;
			if (isChange) {
				url = reverse('api-internal-mfa-change-device');
				action = 'begin_device_change';
			} else {
				url = reverse('api-internal-mfa-enroll');
				action = 'setup_totp';
			}
			const response = await fetch(url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRFToken': getCookie('csrftoken'),
				},
				body: JSON.stringify({ action, method: 'totp' }),
			});
			const data = await response.json();
			if (data.error) {
				await handleErrorResponse(response);
				if (data.error === 'invalid_code_locked_out' || data.error === 'not_authenticated') {
					window.location.href = reverse('mfa-lockout');
				}
			} else {
				setContext((prev) => ({
					...prev,
					qrCodeString: data.qr_code,
					securityKeyString: data.secret || '',
				}));
			}
		} catch (err) {
			handleError(`${STRINGS.errorFetchingSecurityKey}: ${err}`);
		}
	};

	const handleErrorResponse = async (response) => {
		const errorData = await response.json();
		if (response.status === 401 || response.status === 400 || response.status === 403) {
			setError(errorData.error);
			if (errorData.error === 'too_many_attempts' || errorData.error === 'invalid_code_locked_out') {
				setCurrentView('locked-out');
			}
		} else {
			setError('unknownError');
		}
	};

	const handleError = (errorMessage) => {
		setError(errorMessage);
	};

	const handleChangeMethod = () => {
		setCurrentPage(currentPage === 'totp-enrollment' ? 'email-enrollment' : 'totp-enrollment');
		setContext((prev) => ({
			...prev,
			...defaultContext,
		}));
		setError('');
	};

	const handleTryNewQR = async () => {
		await getSecurityKey();
	};

	const handleOnFinish = () => {
		if (!isModal) {
			if (isSafeRedirectUrl(nextUrl)) {
				window.location.href = nextUrl;
			} else {
				window.location.href = '/';
			}
		} else {
			handleClose(true, method);
		}
	};

	return (
		<>
			{!isModal && currentPage !== 'enrollment-complete' && (
				<Box mb={2}>
					<Typography
						variant='link'
						component='a'
						href='/acc/lo/'
						id='logout'
						aria-label='logout'
					>
						{STRINGS.cancelAndLogout}
					</Typography>
				</Box>
			)}
			<Box
				id='mfa-card-1'
				display='flex'
				justifyContent='center'
				alignItems='center'
				role='main'
				aria-labelledby='login-page-title'
				style={{ boxShadow: isModal ? undefined : '0px 2px 8px rgba(0, 0, 0, 0.1)' }}
			>
				<Box
					display='flex'
					justifyContent='space-around'
				>
					<Zoom
						in
					>
						<Box>
							<Box
								style={{
									width: '400px',
									backgroundColor: isModal ? undefined : 'white',
									margin: isModal ? 0 : 'auto',
									minHeight: 320,
								}}
								py='4'
								px='6'
								role='region'
							>
								<Zoom
									in={isLoaded}
									onEnter={() => setIsZooming(true)}
									onExited={() => setIsZooming(false)}
								>
									<Box
										display='flex'
										flexDirection='column'
										justifyContent='center'
										alignItems='center'
									>
										<Box
											display='flex'
											justifyContent='center'
											alignItems='center'
											style={{ width: '400px' }}
										>
											<img
												className='sma-logo-powered'
												src='/media/img/smapply/apply.svg'
												alt='SurveyMonkey Apply logo'
												style={{ maxWidth: '150px', margin: 25 }}
											/>
										</Box>
									</Box>
									<Box
										style={{
											minHeight: 320,
											position: isSliding || isZooming ? 'relative' : 'static',
										}}
									>
										{currentPage === 'method-selection' && (
											<Box display='flex' flexDirection='column' alignItems='center' gap={4} mt={2}>
												<Typography variant='sectionTitleSm' align='center' weight='medium'>
													{STRINGS.selectMFAMethod}
												</Typography>
												<Typography variant='bodySm' align='center'>
													{STRINGS.selectMFAMethodMessage}
												</Typography>
												{methods && methods.map((m) => (
													<Button
														key={`method-selection-${m.method}`}
														onClick={() => { setMethod(methodMap[m.method]); setCurrentPage('challenge'); }}
														id={`method-selection-${m.method}`}
														size='lg'
														style={{ width: '200px' }}
													>
														{m.method === 0 && STRINGS.totpMethod}
														{m.method === 1 && STRINGS.emailMethod}
													</Button>
												))}
											</Box>
										)}
										{currentPage === 'totp-enrollment' && (
											<TOTPEnrollment
												isLoaded={isLoaded}
												context={context}
												setContext={setContext}
												error={error}
												isSliding={isSliding}
												setIsSliding={setIsSliding}
												handleChangeMethod={handleChangeMethod}
												handleErrorResponse={handleErrorResponse}
												handleError={handleError}
												handleTryNewQR={handleTryNewQR}
												currentPage={currentPage}
												setCurrentPage={setCurrentPage}
												isChange={isChange}
											/>
										)}
										{currentPage === 'email-enrollment' && (
											<EmailEnrollment
												isLoaded={isLoaded}
												context={context}
												setContext={setContext}
												error={error}
												setError={setError}
												isSliding={isSliding}
												setIsSliding={setIsSliding}
												handleChangeMethod={handleChangeMethod}
												handleError={handleError}
												currentPage={currentPage}
												setCurrentPage={setCurrentPage}
												isChange={isChange}
												cooldown={cooldown}
												startCooldown={startCooldown}
											/>
										)}
										{currentPage === 'challenge' && (
											<Challenge
												isLoaded={isLoaded}
												context={context}
												setContext={setContext}
												error={error}
												setError={setError}
												message={message}
												isSliding={isSliding}
												setIsSliding={setIsSliding}
												handleErrorResponse={handleErrorResponse}
												handleError={handleError}
												currentPage={currentPage}
												setCurrentPage={setCurrentPage}
												nextUrl={nextUrl}
												method={method}
												isChange={isChange}
												sending={sending}
												cooldown={cooldown}
												sendEmailCode={sendEmailCode}
												startCooldown={startCooldown}
											/>
										)}
										{currentPage === 'enrollment-complete' && (
											<EnrollmentComplete
												isLoaded={isLoaded}
												context={context}
												isSliding={isSliding}
												setIsSliding={setIsSliding}
												currentPage={currentPage}
												handleOnFinish={handleOnFinish}
												finishBtnTxt={isModal ? STRINGS.finish : STRINGS.continueToSite}
												nextUrl={nextUrl}
											/>
										)}
									</Box>
								</Zoom>
							</Box>
						</Box>
					</Zoom>
				</Box>
			</Box>
		</>
	);
};

MFA.propTypes = {
	isModal: PropTypes.bool,
	handleClose: PropTypes.func,
	setCurrentView: PropTypes.func,
	isChange: PropTypes.bool,
};

MFA.defaultProps = {
	isModal: false,
	handleClose: () => {},
	setCurrentView: () => {},
	isChange: false,
};
export default MFA;
